import {
  BankAccountStatus,
  CompanyStatus,
  CompanyVerificationStatus,
  StripePaymentStatus,
} from "@shared_packages/toolkit/types";
import React, { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import styled from "styled-components";
import { AnimateAppearanceWrapper } from "../../../../components/animate-appearence/AnimateAppearanceWrapper";
import PageNotFound from "../../../../components/page-not-found";
import {
  Button,
  ButtonVariant,
  DetailsNav,
  ModalHeader,
  PageTitle,
  Spinner,
  Typography,
  TypographyVariant,
  findFirstVisibleDetailsNavPath,
  flattenDetailsNavRoutes,
} from "../../../../components/ui-kit";
import {
  companiesHouseCompanyUrl,
  shouldShowCompaniesHouseLink,
} from "../../../../constants/companiesHouse";
import { AppRoutes } from "../../../../constants/routes";
import { useAuthUserContext } from "../../../../context/AdministratorContextProvider";
import { useModalManager } from "../../../../context/ModalManager";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import api from "../../../../util/api";
import {
  getApiErrorMessage,
  isNotFoundApiError,
} from "../../../../util/apiErrorMessage";
import { FullCompanyApiType } from "../../../../util/types";
import { useRefreshCompanyLogs } from "../hooks/useGetCompanyLogs";
import { useCompanyDetailsNavConfig } from "./navConfig";

const Section = AppRoutes.Private.CompanyDetailsSection;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopLine = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
  @media (max-width: 900px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 30px;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  @media (max-width: 767px) {
    padding: 14px;
  }
`;

const NavContent = styled.div`
  padding-top: 20px;
`;

const Modal = styled.div`
  max-height: 90vh;
  overflow: auto;
`;

const ModalContent = styled.div`
  padding: 30px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PartnerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 700;
  color: #1976d2;
  background: #e3f2fd;
  white-space: nowrap;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 70vh;
`;

export const CompanyDetails: React.FunctionComponent = () => {
  const { companyId } = useParams<{ companyId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { authUser } = useAuthUserContext();
  const refreshLogs = useRefreshCompanyLogs(companyId!);
  const policies = authUser?.policies;
  // AuthUserContext starts with an all-false placeholder before the real
  // administrator loads (~1.5s delay) — gate hiding on that, not just on
  // the (always-truthy) policies object itself.
  const policiesLoaded = Boolean(authUser?._id);
  const [companyDetails, setCompanyDetails] = useState<FullCompanyApiType>();
  const [notFound, setNotFound] = useState(false);
  const [fatalError, setFatalError] = useState<unknown>(null);
  // Blocks the default-tab redirect below until we know whether the
  // company actually exists — otherwise the URL rewrites to `/overview`
  // (or whatever the default section is) before the 404 check resolves.
  const [initialLoading, setInitialLoading] = useState(true);
  const { addModal, removeLastModal } = useModalManager();
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [partnersErrors, setPartnersErrors] = useState<string[]>([]);

  const getCompany = async (companyId: string) => {
    setNotFound(false);
    setFatalError(null);
    try {
      const result = await api.getCompany(companyId);
      if (result.managedBy) {
        navigate(`${AppRoutes.Private.ManagedCompanies.DETAILS}${companyId}`, {
          replace: true,
        });
        return;
      }
      setCompanyDetails(result);
      setPartnersErrors(markAsPartnerTooltip(result));
    } catch (e: unknown) {
      if (isNotFoundApiError(e)) {
        setNotFound(true);
        return;
      }
      setFatalError(e);
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    setInitialLoading(true);
    getCompany(companyId!);
  }, [companyId]);

  const updateCompanyStatus = async (
    companyId: string,
    body: { status?: CompanyStatus; stripePaymentStatus?: StripePaymentStatus }
  ) => {
    const { status, stripePaymentStatus } = body;
    try {
      await api.updateCompanyStatus(companyId, { status, stripePaymentStatus });
      getCompany(companyId);
      // The status change is audited — surface the new entry right away.
      refreshLogs();
      showNotification("Successfully", NotificationTypes.SUCCESS);
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "An error has occurred"),
        NotificationTypes.WARNING
      );
    }
  };

  const updateCompanyVerificationStatus = async (
    companyId: string,
    status: CompanyVerificationStatus
  ) => {
    try {
      await api.updateCompanyVerificationStatus(companyId, status);
      getCompany(companyId);
      refreshLogs();
      showNotification("Successfully", NotificationTypes.SUCCESS);
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessage(e, "An error has occurred"),
        NotificationTypes.WARNING
      );
    }
  };

  const onMarkAsPartnerConfirm = async () => {
    setLoading(true);
    try {
      await api.markAsPartner(companyId!);
      showNotification(
        "Company has been marked as partner and now has access to partner features",
        NotificationTypes.SUCCESS
      );
    } catch (error: unknown) {
      showNotification(
        getApiErrorMessage(error, "Something went wrong"),
        NotificationTypes.WARNING
      );
      console.log(error);
    }
    setLoading(false);
    removeLastModal();
    getCompany(companyId!);
    refreshLogs();
  };

  const markAsPartnerTooltip = (company: FullCompanyApiType) => {
    const hasVerifiedNonSandBoxBankAccount = company.bankAccounts.some(
      (bankAccount) =>
        bankAccount.status === BankAccountStatus.VERIFIED &&
        !bankAccount.isSandbox
    );
    const messages: string[] = [];
    if (company.canManageCompanies) {
      messages.push("Company is a partner");
      return messages;
    }
    if (!hasVerifiedNonSandBoxBankAccount) {
      messages.push("Company has no verified bank account");
    } else if (
      company.verificationStatus !== CompanyVerificationStatus.VERIFIED
    ) {
      messages.push("Company is not active");
    } else if (company.status !== CompanyStatus.ACTIVE) {
      messages.push("Company is not verified");
    } else if (!company.createdBy.emailVerified) {
      messages.push("Owner email is not verified");
    }
    return messages;
  };

  const onMarkAsPartnerClick = async () => {
    addModal(
      <Modal>
        <ModalHeader title="WARNING" handleClose={removeLastModal} />
        <ModalContent>
          <div>
            <Typography variant={TypographyVariant.BODY1}>
              Are you sure you want to mark this company as a partner?
            </Typography>

            <ButtonWrapper>
              <Button
                variant={ButtonVariant.CONTAINED}
                onClick={onMarkAsPartnerConfirm}
                loading={loading}
                style={{ marginRight: 20 }}
              >
                Confirm
              </Button>
              <Button
                onClick={removeLastModal}
                variant={ButtonVariant.OUTLINED}
              >
                Cancel
              </Button>
            </ButtonWrapper>
          </div>
        </ModalContent>
      </Modal>
    );
  };

  const navTree = useCompanyDetailsNavConfig({
    companyId: companyId!,
    companyDetails,
    policies,
    policiesLoaded,
    partnersErrors,
    onCompanySaved: (updated) => {
      setCompanyDetails(updated);
      setPartnersErrors(markAsPartnerTooltip(updated));
      refreshLogs();
    },
    onActivateClick: async () => {
      await updateCompanyStatus(companyId!, { status: CompanyStatus.ACTIVE });
    },
    onVerifyClick: async () => {
      await updateCompanyVerificationStatus(
        companyId!,
        CompanyVerificationStatus.VERIFIED
      );
    },
    onBanClick: async () => {
      await updateCompanyStatus(companyId!, { status: CompanyStatus.BANNED });
    },
    onVerificationRejectClick: async () => {
      await updateCompanyVerificationStatus(
        companyId!,
        CompanyVerificationStatus.FINAL_REJECTED
      );
    },
    onRefresh: async () => {
      await getCompany(companyId!);
    },
    onMarkAsPartnerClick,
    onEnableStripePaymentClick: async () => {
      await updateCompanyStatus(companyId!, {
        stripePaymentStatus: StripePaymentStatus.ENABLED,
      });
    },
    onDisableStripePaymentClick: async () => {
      await updateCompanyStatus(companyId!, {
        stripePaymentStatus: StripePaymentStatus.DISABLED,
      });
    },
  });

  const detailsBasePath = `${AppRoutes.Private.Companies.DETAILS}${companyId}`;
  const flattenedRoutes = flattenDetailsNavRoutes(navTree);
  const firstVisiblePath =
    findFirstVisibleDetailsNavPath(navTree) ?? Section.OVERVIEW;

  if (fatalError) {
    throw fatalError;
  }

  if (notFound) {
    return <PageNotFound />;
  }

  if (initialLoading) {
    return (
      <LoadingContainer>
        <Spinner height={50} width={50} />
      </LoadingContainer>
    );
  }

  return (
    <AnimateAppearanceWrapper>
      <Container>
        <TopLine>
          <TitleRow>
            <PageTitle
              onBack={() => {
                if (location.key !== "default") navigate(-1);
                else navigate(AppRoutes.Private.Companies.COMPANIES_LIST);
              }}
              title={`Company ${
                companyDetails?.tradingName ?? companyDetails?.name
              }`}
            />
            {companyDetails?.canManageCompanies && (
              <PartnerBadge>Partner</PartnerBadge>
            )}
          </TitleRow>

          <HeaderActions>
            {companyDetails &&
              shouldShowCompaniesHouseLink(
                companyDetails.type,
                companyDetails.identifier
              ) && (
                <Button
                  variant={ButtonVariant.OUTLINED}
                  onClick={() => {
                    window.open(
                      companiesHouseCompanyUrl(companyDetails.identifier!),
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }}
                >
                  View companies house data
                </Button>
              )}
          </HeaderActions>
        </TopLine>
        <TabsContainer>
          <DetailsNav items={navTree} basePath={detailsBasePath} />
          <NavContent>
            <Routes>
              <Route
                index
                element={
                  <Navigate
                    to={`${detailsBasePath}/${firstVisiblePath}`}
                    replace
                  />
                }
              />
              {flattenedRoutes.map(({ path, component, hidden }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    hidden ? (
                      <Navigate
                        to={`${detailsBasePath}/${firstVisiblePath}`}
                        replace
                      />
                    ) : (
                      component
                    )
                  }
                />
              ))}
              <Route
                path="*"
                element={
                  <Navigate
                    to={`${detailsBasePath}/${firstVisiblePath}`}
                    replace
                  />
                }
              />
            </Routes>
          </NavContent>
        </TabsContainer>
        <Typography variant={TypographyVariant.BODY4}></Typography>
      </Container>
    </AnimateAppearanceWrapper>
  );
};
